// GENERATED by helm-console/scripts/ensure-icons.mjs — DO NOT EDIT.
// Source of truth: helm-bridge/src/client/icons-core.ts
// (itself generated from lucide-static by helm-bridge/scripts/gen-icon-sprite.mjs).
// Regenerated on every console build; edits here are overwritten.
// The console and the release UI share ONE icon set on purpose — see the
// header of ensure-icons.mjs.
// icons-core.ts — the sprite PRIMITIVES, with no styled set in the graph.
//
// WHY THIS FILE EXISTS (portal weight, 2026-09-09). `icons.ts` statically
// imports all seven `icons-<style>.ts` modules so that `spriteForStyle()` can
// answer synchronously. That is right for the Helm — one long-lived session UI
// that switches style in place — and wrong for the help portal, a page a
// reader loads once on ONE style. Measured on origin/main (esbuild metafile,
// portal-dist/portal.js 647,322 B): the icon family was 186,713 B, 28.8 % of
// the bundle, of which 159,521 B were the SEVEN styled sets — a reader on one
// style downloaded the other six for nothing, and the portal's weight ceiling
// (660,000 B) had 12,678 B of headroom left for every open PR combined.
//
// The split is a MODULE split, not a fork: every function below used to live
// in icons.ts verbatim and still has exactly one body. What changed is where
// the styled sets come FROM — a mutable registry instead of a static table:
//
//   · `icons.ts`       registers all seven at module scope (static imports).
//                      The Helm, the console copy, and every gate/probe that
//                      bundles icons.ts see byte-identical behaviour.
//   · `icons-lazy.ts`  registers ONE, on demand, through a dynamic import()
//                      esbuild lifts into its own chunk. The portals use this.
//
// A surface that reaches the primitives through `./icons.js` pays for all
// seven sets; a surface that reaches them through `./icons-core.js` pays for
// none until it asks. The portal entry graph is gated on that in build.mjs
// (`[portal-weight] … eager`), so the win cannot be undone by an import.

import { SPRITE } from './icons-sprite.js';

/** The registered styled sets, keyed by style id. Populated by `icons.ts`
 * (all of them, statically) or `icons-lazy.ts` (one at a time, on demand).
 * A style with no entry renders the Blueprint set unchanged — which is the
 * same state a style with no set at all has always had. */
const REGISTERED: Record<string, Record<string, string>> = Object.create(null);

/** Register (or replace) one style's symbol table. Idempotent: re-registering
 * the same set is a no-op in effect, which is what makes the lazy loader safe
 * to call on every repaint. */
export function registerIconSet(styleId: string, symbols: Record<string, string>): void {
  REGISTERED[styleId] = symbols;
}

/** The set registered for a style, or undefined. The one read path — every
 * function below goes through it, so the static and lazy registrations cannot
 * diverge in behaviour. */
export function registeredIconSet(styleId: string): Record<string, string> | undefined {
  return REGISTERED[styleId];
}

/** Every style id with a set registered RIGHT NOW. Under `icons.ts` that is
 * the full table; under `icons-lazy.ts` it grows as styles are visited. */
export function registeredIconSetIds(): string[] {
  return Object.keys(REGISTERED).sort();
}

/** Every id the generated Blueprint sprite defines — the REQUIRED id set a
 * style's sprite has to answer for. Parsed from the sprite itself rather than
 * listed, so it cannot fall out of date. */
export function blueprintIconIds(): string[] {
  const out: string[] = [];
  for (const m of SPRITE.matchAll(/<symbol id="i-([a-z0-9-]+)"/g)) {
    const id = m[1];
    if (id) out.push(id);
  }
  return out;
}

/**
 * Coverage of one style's set against the Blueprint sprite. The numbers the
 * icons gate prints, computed HERE so the gate and the runtime cannot
 * disagree about what ships.
 *
 *   own      — ids this style draws itself
 *   fallback — ids that keep the Blueprint glyph (named, never blank)
 *   orphan   — ids the style defines that the Blueprint sprite does NOT
 *              have. Always a defect: the id would render for this style and
 *              blank everywhere else.
 */
export function iconSetCoverage(styleId: string): {
  total: number;
  own: string[];
  fallback: string[];
  orphan: string[];
} {
  const ids = blueprintIconIds();
  const have = new Set(ids);
  const set = registeredIconSet(styleId) ?? {};
  const own = Object.keys(set).filter((id) => have.has(id)).sort();
  const orphan = Object.keys(set).filter((id) => !have.has(id)).sort();
  const ownSet = new Set(own);
  return { total: ids.length, own, fallback: ids.filter((id) => !ownSet.has(id)), orphan };
}

/**
 * The sprite markup for a style. Blueprint (or any style with no set) gets
 * the generated string unchanged — same bytes, same order, no re-serialise.
 */
export function spriteForStyle(styleId: string): string {
  const set = registeredIconSet(styleId);
  if (!set) return SPRITE;
  // Replace the BODY of each symbol the style re-cuts, in place, so symbol
  // order and every id the style did not touch survive untouched.
  let out = SPRITE;
  for (const [id, art] of Object.entries(set)) {
    const open = `<symbol id="i-${id}" viewBox="0 0 24 24">`;
    const start = out.indexOf(open);
    if (start === -1) continue; // orphan — reported by iconSetCoverage, never rendered
    const end = out.indexOf('</symbol>', start);
    if (end === -1) continue;
    out = out.slice(0, start + open.length) + art + out.slice(end);
  }
  return out;
}

/** Injects the hidden symbol sprite once, ahead of any consumer render.
 * Re-injecting with a different style REPLACES the host (one sprite in the
 * document at a time: two hosts would both define the same ids and the first
 * would silently win).
 *
 * PROVENANCE (style-parity probe). The composition above replaces symbol
 * BODIES in place, so a rendered `<use href="#i-x">` carries no trace of WHICH
 * set drew it: an id the style re-cut and an id that kept the Blueprint
 * artwork are indistinguishable in the DOM. That is precisely the state the
 * owner found in production — "some of these icons are high-fidelity" under
 * MAINFRAME — and nothing in the browser could name it. So every symbol is
 * stamped `data-icon-set` at injection time with the set its ART came from:
 * the style id for an id the style drew, `blueprint` for one that fell back.
 * `e2e/style-parity-probe.mjs` reads exactly this attribute; a styled surface
 * with any `blueprint` stamp among its rendered slots is a RED gate.
 * Stamping happens on the DOM, never on the `spriteForStyle()` STRING, so the
 * identity invariant (blueprint returns SPRITE byte-for-byte) is untouched.
 *
 * The stamp is also the LAZY path's honesty seam: a portal that injected
 * before its style's chunk landed would stamp `blueprint` on every slot and
 * the parity probe would refuse it by name. That is the intended failure —
 * the portals await `loadIconSet()` first (icons-lazy.ts). */
export function injectIconSprite(styleId = 'blueprint'): void {
  const existing = document.getElementById('bridge-icon-sprite');
  if (existing && existing.getAttribute('data-style-set') === styleId) return;
  const host = existing ?? document.createElement('div');
  host.id = 'bridge-icon-sprite';
  host.setAttribute('data-style-set', styleId);
  host.innerHTML = spriteForStyle(styleId);
  const own = new Set(Object.keys(registeredIconSet(styleId) ?? {}));
  for (const sym of Array.from(host.querySelectorAll('symbol[id^="i-"]'))) {
    const id = sym.id.slice(2);
    sym.setAttribute('data-icon-set', own.has(id) ? styleId : 'blueprint');
  }
  if (!existing) document.body.prepend(host);
}

/** `<svg class="icon"><use href="#i-name"/></svg>` helper for HTML strings. */
export function icon(name: string, cls = 'icon'): string {
  return `<svg class="${cls}" aria-hidden="true"><use href="#i-${name}"/></svg>`;
}
